import { createRef, useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'utils/dayjs'
import cn from 'utils/cn'
import Button from 'core/components/buttons/Button'
import Modal from 'core/components/dialogs/Modal'
import APIService from 'api/APIService'
import RatingStars from './RatingStars'
import G2Logo from './G2Logo'
import CloudEvent from 'utils/CloudEvent'
import AnalyticsHelper from 'utils/AnalyticsHelper'
import { useSettings } from 'core/hooks/settings/useSettings'
import useLocalStorage from 'core/hooks/utils/useLocalStorage'
import { useRoles, useUserProfile } from 'core/hooks/data/useDataHook'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import { DEFAULT_DATE_FORMAT } from 'app/settings/defaultValues'
import LinkButton from 'core/components/buttons/LinkButton'
import {
  LINK_EXTERNAL_G2_PRIVACY_POLICY,
  LINK_EXTERNAL_G2_TERMS_OF_USE,
  LINK_GFORM_G2_REVIEW
} from 'constants/Links'

interface RatingModalStorage {
  count: number
}

const daysDifference = 90
const extraOffset = 5
const timeUnit = 'days'

const RatingModal = () => {
  const {
    data: { ratingModal },
    set
  } = useSettings()
  const { data: currentUser } = useUserProfile()
  const { data: roles } = useRoles({
    opts: {
      enabled: !!currentUser?.token_id
    }
  })
  const { data: currentTeam } = useCurrentTeam()

  const [ratingModalStorage, setRatingModalStorage] =
    useLocalStorage<RatingModalStorage>('rating_modal', {
      count: 0
    })

  const [viewsContainerHeight, setViewsContainerHeight] = useState('')
  const [view, setView] = useState<
    'none' | 'initial' | 'improve' | 'g2' | 'g2iFrame'
  >('none')
  const initialDivRef = createRef<HTMLDivElement>()
  const improveDivRef = createRef<HTMLDivElement>()
  const g2DivRef = createRef<HTMLDivElement>()
  const g2iFrameDivRef = createRef<HTMLDivElement>()

  const {
    data: everlanceReviews,
    isSuccess: reviewsSuccess,
    refetch: refetchReviews
  } = useQuery({
    queryKey: ['reviews', 'all'],
    queryFn: () => APIService.misc.getReviews({ pageSize: 1 }),
    gcTime: 0,
    enabled: !!currentUser?.token_id
  })

  const {
    data: g2Reviews,
    isSuccess: g2ReviewsSuccess,
    refetch: refetchG2Reviews
  } = useQuery({
    queryKey: ['reviews', 'g2'],
    queryFn: APIService.misc.g2Reviews,
    enabled: !!currentUser?.token_id
  })

  const { mutate: createReview } = useMutation({
    mutationKey: ['reviews', 'create'],
    mutationFn: APIService.misc.createReview,
    onSuccess() {
      refetchReviews()
    },
    onError(error, variables, context) {
      console.error(error, variables, context)
    }
  })

  const hasPastReviews = useMemo(
    () => reviewsSuccess && !!everlanceReviews?.internal,
    [reviewsSuccess, everlanceReviews?.internal]
  )

  const lastInternalReview = useMemo(() => {
    return everlanceReviews?.internal?.[0]
  }, [everlanceReviews])

  const lastG2Review = useMemo(() => {
    return everlanceReviews?.g2?.[0]
  }, [everlanceReviews])

  const isImmediateReview = useMemo(() => {
    if (lastInternalReview) {
      const secondsSinceLastReview = dayjs().diff(
        dayjs(lastInternalReview.created_at),
        'seconds'
      )
      return secondsSinceLastReview < 15
    }
    return false
  }, [lastInternalReview])

  const isEnoughtTimeToAskAgain = useMemo(() => {
    if (lastInternalReview) {
      const daysSinceLastReview = dayjs().diff(
        dayjs(lastInternalReview.created_at),
        timeUnit
      )
      return daysSinceLastReview > daysDifference
    }
    return true
  }, [lastInternalReview])

  const g2ReviewPending = useMemo(
    () => g2ReviewsSuccess && !!g2Reviews?.url,
    [g2Reviews?.url, g2ReviewsSuccess]
  )

  const showDontRemindMeAgain = useMemo(
    () => ratingModalStorage.count >= 1,
    [ratingModalStorage.count]
  )

  const resetRatingModal = useCallback(() => {
    set('ratingModal', {
      active: false,
      trigger: ''
    })
  }, [set])

  const rate = useCallback(
    async (rate: number) => {
      AnalyticsHelper.trackEvent(CloudEvent.EverlanceReviewRate, {
        $user_id: currentUser?.token_id,
        $role: roles?.join(',') ?? 'none',
        team: currentTeam?.root_team?.company_name ?? 'none',
        rate: rate
      })
      await createReview(rate)
      if (rate > 3) {
        if (g2Reviews?.url) {
          setView('g2')
          AnalyticsHelper.trackEvent(CloudEvent.G2ReviewModalOpen, {
            $user_id: currentUser?.token_id,
            $role: roles?.join(',') ?? 'none',
            trigger: ratingModal.trigger ? 'undefined' : ratingModal.trigger
          })
        } else {
          setTimeout(() => {
            setView('none')
            resetRatingModal()
          }, 300)
        }
      } else {
        setView('improve')
      }
    },
    [
      createReview,
      currentTeam?.root_team?.company_name,
      currentUser?.token_id,
      g2Reviews?.url,
      ratingModal.trigger,
      resetRatingModal,
      roles
    ]
  )

  const onTellUsWhatYouThink = useCallback(() => {
    window.open(LINK_GFORM_G2_REVIEW, '_blank')
    setTimeout(() => {
      resetRatingModal()
      setView('none')
      setRatingModalStorage({
        count: 0
      })
    }, 300)
  }, [resetRatingModal, setRatingModalStorage])

  const onMaybeLater = useCallback(() => {
    if (view === 'g2') {
      setRatingModalStorage({
        count:
          view === 'g2'
            ? (ratingModalStorage.count ?? 0) + 1
            : (ratingModalStorage.count ?? 0)
      })
      AnalyticsHelper.trackEvent(CloudEvent.G2ReviewMaybeLater)
    } else if (view === 'initial') {
      AnalyticsHelper.trackEvent(CloudEvent.EverlanceReviewMaybeLater, {
        $user_id: currentUser?.token_id,
        $role: roles?.join(',') ?? 'none'
      })
    } else if (view === 'g2iFrame') {
      AnalyticsHelper.trackEvent(CloudEvent.G2ReviewClosedOnIframe, {
        $user_id: currentUser?.token_id,
        $role: roles?.join(',') ?? 'none'
      })
    }
    setView('none')
    resetRatingModal()
  }, [
    currentUser?.token_id,
    ratingModalStorage.count,
    resetRatingModal,
    roles,
    setRatingModalStorage,
    view
  ])

  const onContinueToG2 = useCallback(() => {
    if (g2ReviewsSuccess) {
      setTimeout(() => {
        setRatingModalStorage({
          count: 0
        })
      }, 500)
      setView('g2iFrame')
      APIService.misc.g2ReviewMarkAsDone()
      AnalyticsHelper.trackEvent(CloudEvent.G2ReviewWriteAReview)
    }
  }, [g2ReviewsSuccess, setRatingModalStorage])

  const onDontRemindMeAgain = useCallback(() => {
    resetRatingModal()
    setView('none')
    setRatingModalStorage({
      count: 0
    })
    APIService.misc.g2ReviewNotShowAgain()
  }, [resetRatingModal, setRatingModalStorage])

  const onClose = useCallback(() => {
    if (view === 'g2iFrame') {
      refetchG2Reviews()
      onMaybeLater()
    } else {
      resetRatingModal()
      setView('none')
    }
  }, [onMaybeLater, refetchG2Reviews, resetRatingModal, view])

  useEffect(() => {
    if (initialDivRef.current && view === 'initial') {
      const initialDivHeight =
        (initialDivRef.current.scrollHeight ?? 200) + extraOffset
      setViewsContainerHeight(`${initialDivHeight}px`)
    }
  }, [initialDivRef, view])

  useEffect(() => {
    if (view === 'improve') {
      const improveDivHeight =
        (improveDivRef.current?.clientHeight ?? 220) + extraOffset
          ? improveDivRef.current?.clientHeight
          : 190
      setViewsContainerHeight(`${improveDivHeight}px`)
    } else if (view === 'g2') {
      const g2DivHeight =
        (g2DivRef.current?.clientHeight ?? 300) + extraOffset
          ? g2DivRef.current?.clientHeight
          : 336
      setViewsContainerHeight(`${g2DivHeight}px`)
    } else if (view === 'g2iFrame') {
      setViewsContainerHeight('')
    }
  }, [g2DivRef, g2iFrameDivRef, improveDivRef, view])

  useEffect(() => {
    let timeOutId: NodeJS.Timeout
    if (
      ratingModal.active &&
      view === 'none' &&
      !isImmediateReview &&
      g2ReviewPending
    ) {
      if (isEnoughtTimeToAskAgain) {
        setView('initial')
      } else if (
        reviewsSuccess &&
        lastInternalReview &&
        lastInternalReview.stars > 3 &&
        (!lastG2Review ||
          dayjs(lastInternalReview.created_at).format(DEFAULT_DATE_FORMAT) !==
            dayjs(lastG2Review.created_at).format(DEFAULT_DATE_FORMAT))
      ) {
        setView('g2')
        AnalyticsHelper.trackEvent(CloudEvent.G2ReviewModalOpen, {
          $user_id: currentUser?.token_id,
          $role: roles?.join(',') ?? 'none',
          trigger: ratingModal.trigger ? 'undefined' : ratingModal.trigger
        })
      } else {
        timeOutId = setTimeout(() => {
          resetRatingModal()
        }, 5000)
      }
    } else if (view === 'none' && ratingModal.active) {
      timeOutId = setTimeout(() => {
        resetRatingModal()
      }, 5000)
    }
    return () => clearInterval(timeOutId)
  }, [
    g2ReviewPending,
    isEnoughtTimeToAskAgain,
    isImmediateReview,
    lastInternalReview,
    lastG2Review,
    ratingModal.active,
    reviewsSuccess,
    set,
    view,
    resetRatingModal,
    ratingModal.trigger,
    currentUser?.token_id,
    roles
  ])

  useEffect(() => {
    if (currentUser && view === 'initial') {
      AnalyticsHelper.trackEvent(CloudEvent.EverlanceReview, {
        $user_id: currentUser.token_id,
        $role: roles?.join(',') ?? 'none',
        trigger: ratingModal.trigger ? 'undefined' : ratingModal.trigger
      })
    }
  }, [currentUser, ratingModal.trigger, roles, view])

  return (
    <Modal
      show={ratingModal.active && view !== 'none'}
      className={cn(
        'duration-750',
        view === 'g2iFrame'
          ? '!w-[98vw] !max-h-[85vh] !h-[85vh] overflow-hidden'
          : ''
      )}
      onClose={onClose}
    >
      <div
        className={cn(
          'relative overflow-hidden',
          view === 'g2iFrame' ? 'w-full h-full' : ''
        )}
        style={
          viewsContainerHeight
            ? {
                height: viewsContainerHeight
              }
            : {}
        }
      >
        <div
          ref={initialDivRef}
          id="rating-modal-view-initial"
          className={cn(
            'w-full bg-white transition-all duration-500 absolute top-0 left-0 z-0',
            view === 'initial' ? 'z-10 opacity-100' : 'opacity-0'
          )}
        >
          <div className="heading text-center mb-7">Loving Everlance?</div>
          <div className="mb-7">
            <RatingStars
              disabled={!g2ReviewsSuccess || !g2ReviewsSuccess}
              onClick={rate}
            />
          </div>
          <div className="text-center mb-10">
            Let us know how we're doing! Your feedback is important to us.{' '}
            {hasPastReviews && 'Everlance improved in the past 3 months.'}
          </div>
          <div className="text-center">
            <Button variant="outline" onClick={onMaybeLater}>
              Maybe later
            </Button>
          </div>
        </div>

        <div
          ref={improveDivRef}
          id="rating-modal-view-improve"
          className={cn(
            'w-full bg-white transition-all duration-500 absolute top-0 left-0 z-0',
            view === 'improve' ? 'z-10 opacity-100' : 'opacity-0'
          )}
        >
          <div className="heading text-center mb-7">How can we improve?</div>
          <div className="text-center mb-10">
            Looks like there's room for growth. We'd love to know more about
            your experience and how we can do better. Please continue with the
            link below to give us your candid feedback.
          </div>
          <div className="text-center">
            <Button variant="primary" onClick={onTellUsWhatYouThink}>
              Tell us what you think
            </Button>
          </div>
        </div>

        <div
          ref={g2DivRef}
          id="rating-modal-view-g2"
          className={cn(
            'w-full bg-white transition-all duration-500 absolute top-0 left-0 z-0 text-center',
            view === 'g2' ? 'z-10 opacity-100' : 'opacity-0'
          )}
        >
          <div></div>
          <div className="flex flex-col items-center gap-[16px] mb-9">
            <G2Logo />
            <h2 className="m-0 heading">Your feedback helps us get better</h2>
            <p className="m-0">
              As a valued Everlance customer, we´d love for you to review us on
              G2. To continue, your email address will be shared with G2 to
              verify review authenticity.
            </p>
            <p className="m-0">It takes less than 5 minutes!</p>
          </div>
          <div className="flex justify-between items-center mb-7">
            <Button
              variant="outline"
              onClick={onMaybeLater}
              className="ml-[1px] w-[49%]"
            >
              Maybe later
            </Button>
            <Button
              variant="primary"
              onClick={onContinueToG2}
              className="w-[49%]"
              disabled={!g2ReviewsSuccess}
            >
              Continue to G2
            </Button>
          </div>
          {showDontRemindMeAgain && (
            <div className="flex justify-center mb-7">
              <Button
                variant="link"
                onClick={onDontRemindMeAgain}
                className="text-dark font-normal"
              >
                Don’t remind me again
              </Button>
            </div>
          )}
          <div>
            <p className="text-sm">
              Review G2’s{' '}
              <LinkButton
                className="[&_span]:ml-1!"
                text="Privacy Policy"
                url={LINK_EXTERNAL_G2_PRIVACY_POLICY}
              />{' '}
              and{' '}
              <LinkButton
                className="[&_span]:ml-1!"
                text="Terms of Use"
                url={LINK_EXTERNAL_G2_TERMS_OF_USE}
              />
              .
            </p>
          </div>
        </div>

        <div
          ref={g2iFrameDivRef}
          id="rating-modal-view-g2"
          className={cn(
            'w-full bg-white transition-all duration-500 absolute top-0 left-0 z-0 text-center',
            view === 'g2iFrame' ? 'z-10 opacity-100 w-full h-full' : 'opacity-0'
          )}
        >
          <iframe
            title="G2"
            src={g2Reviews?.url}
            className="mt-4 w-full h-[97%]"
          />
        </div>
      </div>
    </Modal>
  )
}

export default RatingModal
